import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Phone, Mail, Home, Building, Palette, MapPin, CheckCircle } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const Index = () => {
  const handleContactClick = () => {
    window.location.href = "tel:0658864706";
  };

  const handleEmailClick = () => {
    window.location.href = "mailto:js.concept@orange.fr";
  };

  const handleDevisClick = () => {
    // Scroll to contact section or navigate to contact page
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If no contact section, navigate to contact page
      window.location.href = '/contact';
    }
  };

  const services = [
    {
      title: "Peinture Intérieure",
      description: "Décoration et peinture de vos espaces intérieurs avec des finitions impeccables",
      icon: Home,
      image: "./images/Interieur 2.jpeg"
    },
    {
      title: "Peinture Extérieure",
      description: "Ravalement de façades et peinture extérieure pour protéger et embellir votre habitat",
      icon: Building,
      image: "./images/Exterieur .png"
    },
    {
      title: "Plafonds & Revêtements",
      description: "Pose de plafonds, faux plafonds et revêtements muraux et de sols",
      icon: Palette,
      image: "./images/Interieur 1.png"
    }
  ];

  const advantages = [
    "Artisan qualifié et expérimenté",
    "Devis gratuit et personnalisé",
    "Matériaux de qualité supérieure",
    "Respect des délais convenus",
    "Garantie sur tous nos travaux",
    "Suivi personnalisé de chaque projet",
    "Intervention Gries et environs, Strasbourg, Haguenau, Saverne, Molsheim, Sélestat"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary-glow to-secondary py-20 lg:py-32">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white animate-fade-in-up">
              <Badge className="mb-4 bg-white/20 text-white border-white/30">
                Entreprise de peinture à Gries
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                JS Concept
                <span className="block text-secondary">Votre partenaire peinture</span>
              </h1>
              <p className="text-xl mb-8 text-white/90 leading-relaxed">
                Spécialiste de la décoration intérieure et de la peinture extérieure dans le Bas-Rhin. 
                Nous intervenons pour la pose de plafonds, revêtements et ravalement de façades.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleDevisClick}
                  size="lg"
                  className="bg-secondary hover:bg-secondary/90 text-white px-8 py-4"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Demander un devis
                </Button>
                <Button 
                  asChild
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-primary px-8 py-4"
                >
                  <Link to="/realisations">
                    Nos réalisations
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://static-us-img.skywork.ai/prod/user/head_picture/2008172270272176128_personne%20.png?image_process=quality,q_90/resize,w_1280/format,webp" 
                alt="Véhicule JS Concept devant une maison" 
                className="rounded-lg shadow-2xl h-[778.65625px] object-cover hero-person-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Nos Services</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Découvrez notre gamme complète de services de peinture et décoration pour tous vos projets
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="group hover:shadow-elegant transition-all duration-300 hover:-translate-y-2">
                  <div className="relative overflow-hidden rounded-t-lg">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full group-hover:scale-105 transition-transform duration-300 h-[222.2554931640625px] object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-primary text-primary-foreground p-2 rounded-lg">
                      <IconComponent className="h-8 w-8" />
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      asChild
                      variant="outline" 
                      className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                    >
                      <Link to="/services">
                        En savoir plus
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Zone d'intervention Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">Intervention dans le Bas-Rhin</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Basée à Gries, notre entreprise JS Concept intervient dans tout le département du Bas-Rhin 
                pour vos travaux de peinture, décoration et ravalement de façades.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {advantages.map((advantage, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                    <span className="text-sm">{advantage}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                asChild
                className="bg-accent hover:bg-accent/90"
              >
                <Link to="/contact">
                  <MapPin className="h-4 w-4 mr-2" />
                  Voir notre zone d'intervention
                </Link>
              </Button>
            </div>
            <div className="relative">
              <img 
                src="./images/Interieur 1.png"
                alt="Salon moderne décoré par JS Concept" 
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact-section" className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Prêt à transformer votre habitat ?</h2>
          <p className="text-xl mb-8 opacity-90">
            Contactez JS Concept dès aujourd'hui pour un devis gratuit et personnalisé. 
            Notre équipe d'experts est à votre écoute pour réaliser vos projets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleContactClick}
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-white px-8 py-4"
            >
              <Phone className="h-5 w-5 mr-2" />
              06 58 86 47 06
            </Button>
            <Button 
              onClick={handleEmailClick}
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white hover:text-primary px-8 py-4"
            >
              <Mail className="h-5 w-5 mr-2" />
              Nous contacter
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
